import React, { useRef } from "react"
import { RankingData } from "../types"
import { formatEFPower } from "../utils/dataProcessor"
import rasterizeHTML from "rasterizehtml"
import { useTranslation } from "react-i18next"
import { useToast } from "../contexts/ToastContext"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import RankCard from "./RankCard"

interface ShareCardProps {
  data: RankingData
  topEFPower: number
  avatar: string
  defaultAvatar: string
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000,
    },
  },
})

function convertDatabaseFormatToEmoji(str: string): string {
  return str.replace(/([0-9])⃣/g, "$1️⃣")
}

const ShareCard: React.FC<ShareCardProps> = ({ data }) => {
  const cardRef = useRef<HTMLDivElement>(null)

  const { t } = useTranslation()
  const { showToast } = useToast()

  const generateCardImage = async (): Promise<string | null> => {
    const cardElement = cardRef.current
    if (!cardElement) return null

    try {
      await document.fonts.ready
      await new Promise((resolve) => setTimeout(resolve, 100))

      const canvas = document.createElement("canvas")
      const width = cardElement.offsetWidth * 2
      const height = cardElement.offsetHeight * 2
      canvas.width = width
      canvas.height = height

      const styles = Array.from(document.styleSheets)
        .map((sheet) => {
          try {
            return Array.from(sheet.cssRules)
              .map((rule) => rule.cssText)
              .join("\n")
          } catch (e) {
            return ""
          }
        })
        .join("\n")

      const htmlWithBg = `
        <div style="width:100%;height:100%;background:rgba(0,0,0,0.8);">
          ${cardElement.outerHTML}
        </div>
      `

      await rasterizeHTML.drawHTML(
        `<style>${styles}</style>${htmlWithBg}`,
        canvas,
        {
          zoom: 2,
          fontLoadTimeout: 2000,
          width,
          height,
        }
      )

      return canvas.toDataURL("image/png")
    } catch (error) {
      console.error("Error generating image:", error)
      return null
    }
  }

  const downloadCard = async () => {
    const dataUrl = await generateCardImage()
    if (!dataUrl) {
      showToast("error", t("toast.imageFailed"))
      return
    }

    const link = document.createElement("a")
    link.download = `EFPower-${convertDatabaseFormatToEmoji(data.ensName)}.png`
    link.href = dataUrl
    link.click()
    showToast("success", t("toast.downloaded"))
  }

  const shareToTwitter = async () => {
    try {
      const dataUrl = await generateCardImage()
      if (!dataUrl) throw new Error("Failed to generate card image")

      const text = encodeURIComponent(
        `🚀 My EFPower is live\n\n` +
          `🏆 Rank  #${data.Rank}\n` +
          `⚡ EFPower  ${formatEFPower(data.EFPower)}\n\n` +
          `🔍 Explore → https://www.efpower.box/?search=${data.listId}`
      )

      const isMobile =
        /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(
          navigator.userAgent
        )

      if (isMobile) {
        const appUrl = `twitter://post?message=${text}`
        const webUrl = `https://x.com/intent/tweet?text=${text}`

        const timeout = setTimeout(() => {
          window.open(webUrl, "_blank")
        }, 500)

        window.location.href = appUrl

        window.addEventListener("blur", () => {
          clearTimeout(timeout)
        })
      } else {
        window.open(`https://x.com/intent/tweet?text=${text}`, "_blank")
      }
      showToast("success", t("toast.twitterOpened"))
    } catch (error) {
      console.error("Error sharing to X:", error)
      showToast("error", t("toast.shareFailed"))
    }
  }

  return (
    <div className="flex flex-col items-center max-h-[90vh] md:max-h-full">
      <QueryClientProvider client={queryClient}>
        <RankCard ref={cardRef} data={data} />

        <div className="flex gap-4 pb-4 flex-shrink-0">
          <button
            onClick={downloadCard}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs md:text-base rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            {t("shareCard.download")}
          </button>
          <button
            onClick={shareToTwitter}
            className="px-6 py-3 bg-gradient-to-r from-black to-gray-800 text-white text-xs md:text-base rounded-lg hover:from-gray-900 hover:to-gray-700 transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
            {t("shareCard.shareToX")}
          </button>
        </div>
      </QueryClientProvider>
    </div>
  )
}

export default ShareCard
